/*
 * What a shared link says to somebody who is not signed in — the nutrition spec, Opening a
 * link.
 *
 * The emoji, the name, what it comes to in calories and who handed it over, and then sign in
 * for the rest of it. Enough to be worth tapping and not the whole product for free: a link is
 * how somebody first meets this app, and a sign-in wall with nothing behind it is a link that
 * says nothing at all.
 *
 * Everything here is read off the link itself and nothing is fetched, which is the same reason
 * every kind carries a name: there is no account yet to read a library food back for.
 */

import { useEffect, useState } from 'react'

import { decodeShare, isEntryShare, nutrientPlaces, type Shared } from '@gymcloud/nutrition/share'

import { formatCalories } from '~/data/nutrition'

/** What it comes to, so far as the link can answer for it on its own. */
function caloriesOf(shared: Shared): number | null {
  if (shared.k === 'food') {
    const stated = shared.f.c?.nu ?? shared.f.nu
    return stated === undefined ? null : (nutrientPlaces(stated)[0] ?? null)
  }
  return null
}

/**
 * The taste, or null where the link carries nothing readable — in which case whoever holds
 * this shows the ordinary sign-in, there being nothing to say about the link.
 */
export function SharedTaste({ payload }: { payload: string }) {
  const [shared, setShared] = useState<Shared | null>(null)

  useEffect(() => {
    let mine = true
    void decodeShare(payload).then((read) => {
      if (mine) {
        setShared(read)
      }
    })
    return () => {
      mine = false
    }
  }, [payload])

  if (shared === null) {
    return null
  }
  const calories = caloriesOf(shared)
  const verb = isEntryShare(shared) ? 'sent you' : 'shared'
  return (
    <div className="mx-auto max-w-sm space-y-2 text-center">
      <p aria-hidden className="text-5xl">
        {shared.e ?? '🍽️'}
      </p>
      <h1 className="text-2xl font-semibold">{shared.n}</h1>
      {shared.by !== undefined && (
        <p className="text-sm text-ink-muted">{`${shared.by} ${verb} this`}</p>
      )}
      {calories !== null && calories > 0 && (
        <p className="text-sm text-ink-muted">{formatCalories(calories)}</p>
      )}
      <p className="pt-2 text-sm text-ink-muted">
        {isEntryShare(shared)
          ? 'Sign in to see what’s in it and log it'
          : 'Sign in to see what’s in it and keep it'}
      </p>
    </div>
  )
}
